import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, FileEdit, Settings as SettingsIcon, ShieldAlert, CalendarClock, Inbox } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const TILES = [
  { to: "/admin/content", icon: FileEdit, title: "Content", desc: "Live WYSIWYG editor for every page", testid: "link-content-manager" },
  { to: "/admin/messages", icon: Inbox, title: "Messages", desc: "Read customer contact submissions", testid: "link-messages" },
  { to: "/admin/schedules", icon: CalendarClock, title: "Schedules", desc: "Plan a publish for later", testid: "link-schedules" },
  { to: "/admin/audit", icon: ShieldAlert, title: "Audit", desc: "Who did what and when", testid: "link-audit" },
  { to: "/admin/settings", icon: SettingsIcon, title: "Settings", desc: "Profile & security", testid: "link-settings" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user, logout: doLogout } = useAuth();

  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  const logout = async () => { await doLogout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="B-Raw" className="h-9 w-auto" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D9381E]">Owner Console</div>
              <div className="font-display font-bold text-sm text-[#111] leading-none">B-Raw</div>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full border-black/10" data-testid="logout-btn">
            <LogOut size={14} className="mr-1"/> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#111]" data-testid="dashboard-title">
          Welcome back{user?.email ? `, ${user.email.split("@")[0]}` : ""}.
        </h1>
        <p className="mt-3 text-[#4B5563]">Pick a section below to manage your site.</p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className="p-6 rounded-3xl bg-white border border-black/10 hover:border-[#D9381E] hover:shadow-lg transition-all group"
              >
                <div className="h-11 w-11 rounded-xl bg-[#111] text-[#F2A900] flex items-center justify-center group-hover:bg-[#D9381E] group-hover:text-white transition-colors">
                  <Icon size={20} />
                </div>
                <div className="mt-4 font-display font-bold text-xl text-[#111]">{t.title}</div>
                <p className="mt-1 text-sm text-[#6B7280]">{t.desc}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
